import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import RangeDatePicker from "../../../components/data/date/RangeDatePicker";
import CustomerSelect from "../../../components/data/customer/CustomerSelect";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import { createDateQuery, createDateRangeQuery, createListQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";

interface QuotationFilterFormProps {
  data?: Partial<EasyTech.Quotation>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export const getQueriesFromFilter = (filters?: Partial<Partial<EasyTech.Quotation>>): SearchQuery[] => {
  const queries: SearchQuery[] = [];
  if (!filters) return queries;
  // Recherche par référence
  if (filters.ref) {
    queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  // Filtre par technicien
  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("technician.id", filters.technician));
    }
  }

  // Filtre par client
  if (filters.customer) {
    if (Array.isArray(filters.customer)) {
      const customerIds = filters.customer.map((c: any) => typeof c === "string" ? c : c.id);
      if (customerIds.length === 1) {
        queries.push(createSingleQuery("customer.id", customerIds[0]));
      } else if (customerIds.length > 1) {
        queries.push(createListQuery("customer.id", customerIds));
      }
    } else if (typeof filters.customer === "object" && (filters.customer as any).id) {
      queries.push(createSingleQuery("customer.id", (filters.customer as any).id));
    } else if (typeof filters.customer === "string") {
      queries.push(createSingleQuery("customer.id", filters.customer));
    }
  }

  // Filtre par date
  if (filters.startDate || filters.endDate) {
    if (filters.startDate && filters.endDate) {
      queries.push(createDateRangeQuery("date", new Date(filters.startDate), new Date(filters.endDate), "BETWEEN"));
    } else if (filters.startDate) {
      queries.push(createDateQuery("date", new Date(filters.startDate), "GREATER_THAN"));
    } else if (filters.endDate) {
      queries.push(createDateQuery("date", new Date(filters.endDate), "LESS_THAN"));
    }
  }

  if (filters.disabled)
    queries.push(createSingleQuery("enabled", false));
  return queries;
};

/**
 * Formulaire de filtrage des devis
 * Permet de filtrer par référence, client, date et technicien
 * Utilisé dans le drawer de filtrage des devis
 */
const QuotationFilterForm = (props: QuotationFilterFormProps) => {
  // État local du formulaire pour gérer les valeurs des champs
  const [formData, setFormData] = useState<Partial<EasyTech.Quotation>>(
    props.data || {}
  );

  // Met à jour l'état local quand les props changent
  useEffect(() => {
    setFormData(props.data || {});
  }, [props.data]);

  /**
   * Gère les changements pour les champs simples
   * Met à jour l'état local et notifie le parent du changement
   * @param value - Nouvelle valeur
   * @param name - Nom du champ
   */
  const handleChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    // Pour customer et technician, toujours un tableau
    if (["customer", "technician"].includes(name)) {
      tmp[name as keyof Partial<EasyTech.Quotation>] = Array.isArray(value) ? value : [value];
    } else {
      tmp[name as keyof Partial<EasyTech.Quotation>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.Quotation>]);
    setFormData(tmp);
  };

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    const tmp = structuredClone(formData);
    tmp.startDate = start || undefined;
    tmp.endDate = end || undefined;
    props.onChange("startDate", tmp.startDate);
    props.onChange("endDate", tmp.endDate);
    setFormData(tmp);
  };

  /**
   * Soumission du formulaire
   * Prévient le comportement par défaut et appelle la fonction onSubmit du parent
   */
  const submit = (e: any) => {
    e.preventDefault();
    if (formData.startDate && !formData.endDate) return;
    if (formData.endDate && !formData.startDate) return;
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-quotation-filter"}>
      <div className="grid grid-cols-1 gap-4">
        {/* Champ de référence */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("common:ref:placeholder")}
          />
        </div>

        {/* Sélecteur de client */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("quotation:form:fields:customer:label")}</label>
          <CustomerSelect
            onChange={(customers) => handleChange(customers, "customer")}
            value={Array.isArray(formData.customer) ? formData.customer : (formData.customer ? [formData.customer] : undefined)}
            isClearable={true}
            isMulti={true}
          />
        </div>

        {/* Sélecteur de plage de dates */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters.creationDate.label")}</label>
          <div className="flex-1 w-full">
            <RangeDatePicker
              startDate={formData.startDate}
              endDate={formData.endDate}
              onChange={handleDateRangeChange}
              withTime={true}
            />
          </div>
        </div>

        {/* Sélecteur de technicien */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("quotation:form:fields:technician:label")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={Array.isArray(formData.technician) ? formData.technician : (formData.technician ? [formData.technician] : [])}
            clearable={true}
            isMulti={true}
          />
        </div>
      </div>
    </form>
  );
};

export default QuotationFilterForm; 